// useWalletAlarms — Per-Wallet Threshold-basierte Browser-Notifications.
//
// Unterschied zu useWhaleNotifier (Cluster C):
//   - useWhaleNotifier: ALLE Whale-Alerts (HL-WS ≥$1M global)
//   - useWalletAlarms: NUR Alerts für getrackte Wallets, gegen pro-Wallet
//     Threshold geprüft. Wenn z.B. Wallet X einen Trade ≥ $50k öffnet UND
//     ihr Threshold $50k ist → Notification.
//
// Mechanik:
//   - localStorage-Flag `eve_wallet_alarms_enabled` (Master-Switch, getrennt
//     vom globalen Whale-Alarm)
//   - Subscribe auf useWhaleAlertsStore + useWalletMonitor.wallets
//   - Pro Alert: wenn alert.wallet in tracked-wallets UND sizeUsd ≥ threshold:
//     → Notification feuern (Browser-API)
//   - Anti-Spam: max 1 Notification pro 1s pro Wallet
//   - Tab-Visibility-Check (keine Notify wenn Doc gerade hinschaut)

import { useCallback, useEffect, useRef, useState } from 'react';
import { selectWhaleAlerts, useWhaleAlertsStore } from '@/stores/whale-alerts-store';
import type { WatchedWallet } from '@/types/wallet-monitor';
import type { WhaleAlert } from '@/types/hyperliquid';

const STORAGE_KEY = 'eve_wallet_alarms_enabled';
const PER_WALLET_RATE_LIMIT_MS = 1_000;

export interface UseWalletAlarmsOptions {
  /** Aktuell getrackte Wallets aus useWalletMonitor. */
  wallets: ReadonlyArray<WatchedWallet>;
}

export interface UseWalletAlarmsReturn {
  enabled: boolean;
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  toggle: () => void;
}

export function useWalletAlarms({ wallets }: UseWalletAlarmsOptions): UseWalletAlarmsReturn {
  const supported =
    typeof window !== 'undefined' && typeof window.Notification !== 'undefined';

  const [enabledFlag, setEnabledFlag] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    () => (supported ? Notification.permission : 'unsupported'),
  );

  const lastNotifiedAtRef = useRef<Map<string, number>>(new Map());
  const seenKeysRef = useRef<Set<string>>(new Set());

  const enabled = enabledFlag && permission === 'granted';

  const toggle = useCallback(() => {
    if (!supported) return;
    if (enabledFlag) {
      setEnabledFlag(false);
      window.localStorage.setItem(STORAGE_KEY, '0');
      return;
    }
    if (Notification.permission === 'granted') {
      setPermission('granted');
      setEnabledFlag(true);
      window.localStorage.setItem(STORAGE_KEY, '1');
      return;
    }
    if (Notification.permission === 'denied') {
      setPermission('denied');
      return;
    }
    void Notification.requestPermission().then((result) => {
      setPermission(result);
      if (result === 'granted') {
        setEnabledFlag(true);
        window.localStorage.setItem(STORAGE_KEY, '1');
      }
    });
  }, [enabledFlag, supported]);

  useEffect(() => {
    if (!supported || !enabled || wallets.length === 0) return;
    // Lookup-Map address → {label, threshold} damit der Subscribe-Callback
    // schnell ist (O(1) pro Alert).
    const byAddr = new Map<string, { label: string; threshold: number }>();
    for (const w of wallets) {
      byAddr.set(w.address.toLowerCase(), {
        label: w.label?.trim() || w.address.slice(0, 6),
        threshold: w.threshold_usd ?? 1000,
      });
    }
    const unsub = useWhaleAlertsStore.subscribe((state) => {
      const alerts = selectWhaleAlerts(state);
      for (const alert of alerts) {
        if (!alert.wallet) continue;
        const key = alertKey(alert);
        if (seenKeysRef.current.has(key)) continue;
        seenKeysRef.current.add(key);
        const meta = byAddr.get(alert.wallet.toLowerCase());
        if (!meta) continue;
        if (alert.sizeUsd < meta.threshold) continue;
        const now = Date.now();
        const last = lastNotifiedAtRef.current.get(alert.wallet) ?? 0;
        if (now - last < PER_WALLET_RATE_LIMIT_MS) continue;
        lastNotifiedAtRef.current.set(alert.wallet, now);
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          continue;
        }
        try {
          new Notification(`👁 ${meta.label} · ${alert.side} ${alert.coin}`, {
            body: `$${(alert.sizeUsd / 1_000_000).toFixed(1)}M · Schwelle $${meta.threshold.toLocaleString('en-US')}`,
            icon: '/eve-satoshi.png',
            tag: `wallet-${alert.wallet}`,
          });
        } catch (err) {
          console.warn('[wallet-alarms] Notification failed:', err);
        }
      }
    });
    return () => {
      unsub();
    };
  }, [enabled, supported, wallets]);

  return { enabled, supported, permission, toggle };
}

function alertKey(a: WhaleAlert): string {
  return `${a.time}-${a.coin}-${a.wallet ?? a.hash ?? ''}`;
}
